/**
 * DecisionFlow — AIGovernanceManager
 * ui/src/components/tenant-admin/AIGovernanceManager.jsx
 * S1034 — Agent Operating Mode Governance
 *
 * Surface: TenantAdminShell — "AI Governance" screen (tab 5)
 * RBAC:    tenant_super_admin always.
 *          comp_admin only if ca_permitted_modes non-empty OR ca_kill_switch_enabled=TRUE.
 *
 * Endpoints:
 *   GET  /api/v1/tenant-ai-mode            — read mode state + recent changes
 *   PUT  /api/v1/tenant-ai-mode            — switch current_mode (T1/T2)
 *   PUT  /api/v1/tenant-ai-mode/ceiling    — set ceiling + CA delegation (Super Admin only)
 *   POST /api/v1/tenant-ai-mode/kill-switch — activate/deactivate tenant kill switch
 *
 * Spec: spec_agent_operating_mode_s1033.md §8.2
 * DECISION-S1033-02: kill switch endpoint separate from mode switching.
 * DECISION-S1033-04: kill switch is circuit breaker — does NOT change current_mode.
 * AD-S1033-REVOCATION-CONFIRMATION: CA revocation requires confirmation dialog.
 *
 * Design rules (non-negotiable):
 *   CSS: rgb(var(--color-*)) only — no hardcoded hex
 *   Text sizes: text-tok-*
 *   Tables: max-h-* + overflow-y-auto + sticky thead
 *   Modal does NOT close on outside click (NN-07)
 *   reason field always required — never optional
 */

import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext.jsx";
import Button from "../shared/Button.jsx";
import Card from "../shared/Card.jsx";

const API_BASE = "/api/v1/tenant-ai-mode";
const REASON_MIN = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function truncateId(id) {
  if (!id) return "—";
  return id.slice(0, 8) + "…";
}

const MODE_LABELS = {
  T0: "T0 — Agent Off",
  T1: "T1 — Advise Only",
  T2: "T2 — Full Copilot",
};

const MODE_DESCRIPTIONS = {
  T0: "No AI agent activity. No sessions, no tool calls, no tokens consumed.",
  T1: "Agent reads and explains platform state. No governed action proposals or write actions.",
  T2: "Full Copilot — all Gates 0–8 available. Proposals, write actions, and remediations enabled.",
};

const CHANGE_TYPE_LABELS = {
  mode_change:       "Mode change",
  ceiling_change:    "Ceiling change",
  delegation_change: "Delegation change",
  kill_switch_on:    "Kill switch activated",
  kill_switch_off:   "Kill switch deactivated",
};

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function ReasonField({ value, onChange, placeholder, disabled }) {
  const chars = value.trim().length;
  return (
    <div className="space-y-1">
      <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
        Reason <span className="text-[rgb(var(--color-danger))]">*</span>
      </label>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? "Required — stored in audit trail…"}
        disabled={disabled}
        className="w-full resize-none rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
      />
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        {chars}/{REASON_MIN} characters minimum
      </p>
    </div>
  );
}

function ErrorNote({ message }) {
  if (!message) return null;
  return (
    <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/8 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message}
    </div>
  );
}

function SuccessNote({ message }) {
  if (!message) return null;
  return (
    <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/8 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KillSwitchBanner — prominent status at top of page
// ---------------------------------------------------------------------------

function KillSwitchBanner({ isActive }) {
  if (isActive) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-4 py-4">
        <div className="flex items-start gap-3">
          <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-danger))]" />
          <div>
            <p className="text-tok-sm font-bold text-[rgb(var(--color-danger))]">
              Kill switch ACTIVE — agent halted for this tenant
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              All new agent sessions are rejected. Mode switching is disabled while halted.
              Deactivate the kill switch to resume — the agent will return to its pre-halt mode.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-4 py-3">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
          Agent operating normally — kill switch inactive
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModeBadge
// ---------------------------------------------------------------------------

function ModeBadge({ mode }) {
  const tones = {
    T0: "text-[rgb(var(--color-danger))] bg-[rgb(var(--color-danger))]/8 border-[rgb(var(--color-danger))]/30",
    T1: "text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/8 border-[rgb(var(--color-warning))]/30",
    T2: "text-[rgb(var(--color-success))] bg-[rgb(var(--color-success))]/8 border-[rgb(var(--color-success))]/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-tok-xs font-bold ${tones[mode] ?? tones.T2}`}>
      {MODE_LABELS[mode] ?? mode}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section 1: Operating Mode + Kill Switch
// ---------------------------------------------------------------------------

function OperatingModeSection({ modeData, isSuperAdmin, onRefresh }) {
  const [modeReason, setModeReason] = useState("");
  const [pendingMode, setPendingMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Kill switch state
  const [ksReason, setKsReason] = useState("");
  const [ksSaving, setKsSaving] = useState(false);
  const [ksError, setKsError] = useState(null);
  const [ksModal, setKsModal] = useState(null); // 'activate' | 'deactivate'

  const isActive = modeData.is_kill_switch_active;
  const currentMode = modeData.current_mode;
  const ceiling = modeData.mode_ceiling;
  const permitted = modeData.ca_permitted_modes ?? [];

  // Determine which modes are available to select
  const modeOrder = { T0: 0, T1: 1, T2: 2 };
  const ceilingRank = modeOrder[ceiling] ?? 2;
  const availableModes = ["T1", "T2"].filter(
    (m) => modeOrder[m] <= ceilingRank && (isSuperAdmin || permitted.includes(m))
  );

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  async function handleModeSwitch() {
    if (!pendingMode || modeReason.trim().length < REASON_MIN) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(API_BASE, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_mode: pendingMode, reason: modeReason.trim() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `Error ${res.status}`);
      setToast(`Mode changed to ${MODE_LABELS[pendingMode]}.`);
      setPendingMode("");
      setModeReason("");
      onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleKillSwitch(action, reason) {
    setKsSaving(true);
    setKsError(null);
    try {
      const res = await fetch(`${API_BASE}/kill-switch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, reason: reason.trim() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `Error ${res.status}`);
      setToast(action === "activate" ? "Kill switch activated. Agent halted." : "Kill switch deactivated. Agent resuming.");
      setKsModal(null);
      setKsReason("");
      onRefresh();
    } catch (e) {
      setKsError(e.message);
    } finally {
      setKsSaving(false);
    }
  }

  const canSwitchMode = !isActive && pendingMode && pendingMode !== currentMode && modeReason.trim().length >= REASON_MIN;
  const canKillSwitch = isSuperAdmin || modeData.ca_kill_switch_enabled;

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="fixed right-5 top-5 z-50 max-w-sm rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 shadow-lg text-tok-sm text-[rgb(var(--color-text))]">
          {toast}
        </div>
      )}

      {/* Kill switch modal */}
      {ksModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">
            <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${
              ksModal === "activate"
                ? "border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/8"
                : "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/8"
            }`}>
              {ksModal === "activate"
                ? <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-danger))]" />
                : <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
              }
              <div>
                <p className="text-tok-sm font-bold text-[rgb(var(--color-text))]">
                  {ksModal === "activate" ? "Halt agent for this tenant?" : "Resume agent for this tenant?"}
                </p>
                <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {ksModal === "activate"
                    ? "All new agent sessions will be rejected immediately. Current_mode is preserved — the agent will return to its current mode when you resume."
                    : "The agent will resume in its current operating mode immediately."
                  }
                </p>
              </div>
            </div>
            <ReasonField
              value={ksReason}
              onChange={setKsReason}
              placeholder={
                ksModal === "activate"
                  ? "e.g. Pausing AI during Q3 internal audit…"
                  : "e.g. Audit complete — resuming normal operations…"
              }
              disabled={ksSaving}
            />
            {ksError && <ErrorNote message={ksError} />}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setKsModal(null); setKsReason(""); }} disabled={ksSaving}>
                Cancel
              </Button>
              <button
                type="button"
                onClick={() => handleKillSwitch(ksModal, ksReason)}
                disabled={ksReason.trim().length < REASON_MIN || ksSaving}
                className={`rounded-xl px-4 py-2 text-tok-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${
                  ksModal === "activate"
                    ? "bg-[rgb(var(--color-danger))] text-white hover:opacity-90"
                    : "bg-[rgb(var(--color-success))] text-white hover:opacity-90"
                }`}
              >
                {ksSaving
                  ? (ksModal === "activate" ? "Halting…" : "Resuming…")
                  : (ksModal === "activate" ? "Halt Agent" : "Resume Agent")
                }
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Kill switch banner — always first */}
        <KillSwitchBanner isActive={isActive} />

        {/* Kill switch control */}
        {canKillSwitch && (
          <div className="flex items-center gap-3">
            {isActive ? (
              <button
                type="button"
                onClick={() => setKsModal("deactivate")}
                className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-4 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/15 transition-colors"
              >
                <ShieldCheck className="h-4 w-4" />
                Resume Agent
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setKsModal("activate")}
                className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-4 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/15 transition-colors"
              >
                <ShieldOff className="h-4 w-4" />
                Halt Agent
              </button>
            )}
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Emergency stop. Does not change operating mode.
            </p>
          </div>
        )}

        {/* Mode selector — disabled when kill switch active */}
        <div className={`rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 space-y-4 ${isActive ? "opacity-60 pointer-events-none" : ""}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Operating Mode</p>
              <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                Ceiling: <ModeBadge mode={ceiling} />
              </p>
            </div>
            <ModeBadge mode={currentMode} />
          </div>

          {isActive && (
            <p className="text-tok-xs text-[rgb(var(--color-warning))] font-medium">
              Mode switching unavailable while agent is halted — resume first.
            </p>
          )}

          {!isActive && availableModes.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-2">
                {availableModes.map((mode) => (
                  <label
                    key={mode}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 transition ${
                      pendingMode === mode
                        ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                        : "border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]"
                    } ${mode === currentMode ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <input
                      type="radio"
                      name="pending_mode"
                      value={mode}
                      checked={pendingMode === mode}
                      onChange={() => mode !== currentMode && setPendingMode(mode)}
                      disabled={mode === currentMode || saving}
                      className="mt-0.5 shrink-0"
                    />
                    <div>
                      <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                        {MODE_LABELS[mode]}
                        {mode === currentMode && (
                          <span className="ml-2 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">(current)</span>
                        )}
                      </p>
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                        {MODE_DESCRIPTIONS[mode]}
                      </p>
                    </div>
                  </label>
                ))}
              </div>

              {pendingMode && pendingMode !== currentMode && (
                <div className="space-y-3">
                  <ReasonField
                    value={modeReason}
                    onChange={setModeReason}
                    placeholder={`Reason for switching to ${MODE_LABELS[pendingMode]}…`}
                    disabled={saving}
                  />
                  {error && <ErrorNote message={error} />}
                  <div className="flex items-center gap-2">
                    <Button onClick={handleModeSwitch} disabled={!canSwitchMode || saving}>
                      {saving ? "Applying…" : `Apply — ${MODE_LABELS[pendingMode]}`}
                    </Button>
                    <Button variant="secondary" onClick={() => { setPendingMode(""); setModeReason(""); setError(null); }} disabled={saving}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isActive && availableModes.length === 0 && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              No modes available to switch to. Contact Super Admin to update delegation.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section 2: Ceiling + CA Delegation (Super Admin only)
// ---------------------------------------------------------------------------

function CeilingDelegationSection({ modeData, onRefresh }) {
  const [ceilingValue, setCeilingValue] = useState(modeData.mode_ceiling);
  const [caPermitted, setCaPermitted] = useState(modeData.ca_permitted_modes ?? []);
  const [caKsEnabled, setCaKsEnabled] = useState(modeData.ca_kill_switch_enabled ?? false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset local state when modeData changes
  useEffect(() => {
    setCeilingValue(modeData.mode_ceiling);
    setCaPermitted(modeData.ca_permitted_modes ?? []);
    setCaKsEnabled(modeData.ca_kill_switch_enabled ?? false);
  }, [modeData]);

  function togglePermitted(mode) {
    setCaPermitted((prev) =>
      prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]
    );
  }

  async function handleSave() {
    if (reason.trim().length < REASON_MIN) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/ceiling`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode_ceiling:           ceilingValue,
          ca_permitted_modes:     caPermitted,
          ca_kill_switch_enabled: caKsEnabled,
          reason:                 reason.trim(),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `Error ${res.status}`);
      setSuccess("Ceiling and delegation updated.");
      setReason("");
      onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Mode ceiling */}
      <div className="space-y-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Mode Ceiling
        </p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Maximum mode this tenant can operate in. No path can exceed this ceiling.
        </p>
        <div className="flex gap-2">
          {["T1", "T2"].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setCeilingValue(mode)}
              disabled={saving}
              className={`rounded-xl border px-4 py-2 text-tok-sm font-semibold transition ${
                ceilingValue === mode
                  ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
                  : "border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              }`}
            >
              {MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      {/* CA delegation — mode switching */}
      <div className="space-y-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Comp Admin — Mode Switching
        </p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          Which modes Comp Admin is permitted to switch to. Empty = no mode switching access.
        </p>
        <div className="flex gap-2">
          {["T1", "T2"].map((mode) => (
            <label key={mode} className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={caPermitted.includes(mode)}
                onChange={() => togglePermitted(mode)}
                disabled={saving}
                className="h-4 w-4 rounded border-[rgb(var(--color-border))]"
              />
              <span className="text-tok-sm text-[rgb(var(--color-text))]">{MODE_LABELS[mode]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* CA delegation — kill switch */}
      <div className="space-y-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Comp Admin — Kill Switch Authority
        </p>
        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={caKsEnabled}
            onChange={(e) => setCaKsEnabled(e.target.checked)}
            disabled={saving}
            className="h-4 w-4 rounded border-[rgb(var(--color-border))]"
          />
          <div>
            <p className="text-tok-sm text-[rgb(var(--color-text))]">
              Allow Comp Admin to activate and deactivate the kill switch
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Independent of mode switching permission. When enabled, CA sees a Halt Agent button.
            </p>
          </div>
        </label>
      </div>

      <ReasonField
        value={reason}
        onChange={setReason}
        placeholder="Reason for changing ceiling or delegation…"
        disabled={saving}
      />
      {error   && <ErrorNote message={error} />}
      {success && <SuccessNote message={success} />}
      <Button onClick={handleSave} disabled={reason.trim().length < REASON_MIN || saving}>
        {saving ? "Saving…" : "Update Ceiling & Delegation"}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3: Change History
// ---------------------------------------------------------------------------

function ChangeHistorySection({ changes }) {
  if (!changes || changes.length === 0) {
    return (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No changes recorded yet.</p>
    );
  }

  return (
    <div className="max-h-80 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
      <table className="w-full text-tok-sm">
        <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
          <tr>
            {["Date / Time", "Change", "From", "To", "Role", "Delegated", "Reason"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[rgb(var(--color-border))]">
          {changes.map((c) => (
            <tr key={c.change_id} className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]">
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                {formatDate(c.changed_at)}
              </td>
              <td className="px-3 py-2.5 text-tok-xs font-medium text-[rgb(var(--color-text))] whitespace-nowrap">
                {CHANGE_TYPE_LABELS[c.change_type] ?? c.change_type}
              </td>
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {c.from_mode ?? c.from_ceiling ?? "—"}
              </td>
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text))]">
                {c.to_mode ?? c.to_ceiling ?? "—"}
              </td>
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                {c.changed_by_role}
              </td>
              <td className="px-3 py-2.5 text-center">
                {c.delegation_used
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-[rgb(var(--color-success))]" />
                  : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>
                }
              </td>
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text))] max-w-xs truncate" title={c.reason}>
                {c.reason ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AIGovernanceManager root
// ---------------------------------------------------------------------------

export default function AIGovernanceManager() {
  const { user } = useAuth();
  const isSuperAdmin = user?.user_type === "tenant_super_admin";

  const [modeData, setModeData] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const loadModeData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(API_BASE, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      setModeData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadModeData(); }, [loadModeData]);

  return (
    <div className="space-y-4">

      {/* Section 1: Status + Kill Switch + Mode */}
      <Card
        title="Agent Operating Mode"
        action={
          <button
            type="button"
            onClick={loadModeData}
            disabled={loading}
            className="text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline disabled:opacity-40"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        }
      >
        <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Control the operating mode for this tenant's AI Copilot.
          Kill switch is an emergency stop — it does not change the configured mode.
          Mode changes apply to new sessions only. Active sessions are unaffected.
        </p>

        {loading && (
          <div className="animate-pulse space-y-3">
            <div className="h-14 rounded-xl skeleton" />
            <div className="h-24 rounded-xl skeleton" />
          </div>
        )}

        {error && !loading && (
          <div className="space-y-2">
            <ErrorNote message={`Unable to load operating mode: ${error}`} />
            <button type="button" onClick={loadModeData} className="text-tok-xs underline hover:opacity-80">
              Retry
            </button>
          </div>
        )}

        {modeData && !loading && (
          <OperatingModeSection
            modeData={modeData}
            isSuperAdmin={isSuperAdmin}
            onRefresh={loadModeData}
          />
        )}
      </Card>

      {/* Section 2: Ceiling + Delegation — Super Admin only */}
      {isSuperAdmin && modeData && !loading && (
        <Card title="Mode Ceiling & Comp Admin Delegation">
          <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Set the maximum operating mode for this tenant and control what Comp Admin is permitted to govern.
            Lowering the ceiling auto-lowers current mode if it exceeds the new ceiling.
          </p>
          <CeilingDelegationSection modeData={modeData} onRefresh={loadModeData} />
        </Card>
      )}

      {/* Section 3: Change History */}
      {modeData && (modeData.recent_changes?.length > 0) && (
        <Card title="Change History">
          <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Last {modeData.recent_changes.length} governance changes for this tenant.
          </p>
          <ChangeHistorySection changes={modeData.recent_changes} />
        </Card>
      )}
    </div>
  );
}
